/* eslint-disable no-unused-vars */
import { useMemo } from "react";
import { useRouter } from "next/router";

interface QueryParams {
  [key: string]: string | string[];
}

export enum NAVIGATION_MODE {
  PUSH = "push",
  REPLACE = "replace",
}
const useQueryParams = () => {
  const router = useRouter();
  const { pathname, query } = router;

  const pushNavigator = (
    queryString: string | undefined,
    shallow: boolean = true,
    mode: NAVIGATION_MODE = NAVIGATION_MODE.PUSH
  ) => {
    router[mode](
      {
        pathname,
        ...(queryString && { query: decodeURIComponent(queryString) }),
      },
      undefined,
      {
        shallow,
      }
    );
  };
  const utils = useMemo(() => {
    const queryParamsInstance = new URLSearchParams(
      Object.entries(query).reduce((acc, [key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => acc.append(key, item));
        } else {
          acc.append(key, value || "");
        }
        return acc;
      }, new URLSearchParams())
    );

    return {
      setParams(params: {
        data: { key: string; value: string | string[] }[];
        redirect?: boolean;
        shallow?: boolean;
        mode?: NAVIGATION_MODE;
        clear?: boolean;
      }) {
        const {
          data,
          redirect = false,
          shallow = true,
          mode = NAVIGATION_MODE.PUSH,
          clear = false,
        } = params;
        if (clear) {
          Array.from(queryParamsInstance.keys())?.forEach((key) => {
            queryParamsInstance.delete(key);
          });
        }
        data.forEach(({ key, value }) => {
          if (queryParamsInstance.get(key)) queryParamsInstance.delete(key);
          if (Array.isArray(value)) {
            value.forEach((v) => queryParamsInstance.append(key, v));
          } else {
            queryParamsInstance.append(key, value);
          }
        });

        const queryString = queryParamsInstance.toString();
        if (redirect) pushNavigator(queryString, shallow, mode);

        return queryString;
      },
      setParam(data: {
        key: string;
        value: string | string[];
        redirect?: boolean;
        shallow?: boolean;
        replace?: boolean;
        clear?: boolean;
      }) {
        const {
          key,
          value,
          redirect = true,
          shallow = false,
          replace = false,
          clear = false,
        } = data;
        if (clear) {
          Array.from(queryParamsInstance.keys())?.forEach((key) => {
            queryParamsInstance.delete(key);
          });
        }
        const isExist = queryParamsInstance.get(key);
        if (isExist && replace) {
          queryParamsInstance.delete(key);
          queryParamsInstance.append(
            key,
            Array.isArray(value) ? value.join(",") : value
          );
        } else if (isExist) {
          queryParamsInstance.delete(key);
          queryParamsInstance.append(
            key,
            `${isExist},${Array.isArray(value) ? value.join(",") : value}`
          );
        } else {
          if (Array.isArray(value)) {
            queryParamsInstance.append(key, value.join(","));
          } else {
            queryParamsInstance.append(key, value);
          }
        }

        const queryString = queryParamsInstance.toString();
        if (redirect) pushNavigator(queryString, shallow);

        return queryString;
      },
      getParam(key: string) {
        return queryParamsInstance.get(key);
      },
      removeParamByKey(
        key: string | string[],
        redirect = false,
        shallow: boolean = true
      ) {
        if (Array.isArray(key)) {
          key.forEach((keyItem) => queryParamsInstance.delete(keyItem));
        } else {
          queryParamsInstance.delete(key);
        }
        const queryString = queryParamsInstance.toString();
        if (redirect) pushNavigator(queryString, shallow);
        return queryString;
      },
      removeParamByValue(
        key: string,
        value: string,
        redirect = false,
        shallow: boolean = true
      ) {
        const isExist = queryParamsInstance.get(key);

        if (isExist?.includes(",")) {
          queryParamsInstance.delete(key);

          let replaceText = isExist
            .replace(`,${value}`, "")
            .replace(value, "")
            .replace(/^,|,$/g, ""); // Remove leading or trailing commas

          if (replaceText) {
            queryParamsInstance.append(key, replaceText);
          }
        } else {
          //@ts-ignore
          queryParamsInstance.delete(key, value); // Assuming a custom delete method
        }

        const queryString = queryParamsInstance.toString();

        if (redirect) pushNavigator(queryString, shallow);
        return queryString;
      },
      clearParams(redirect = false, shallow: boolean = true) {
        Array.from(queryParamsInstance.keys())?.forEach((key) => {
          queryParamsInstance.delete(key);
        });

        const queryString = queryParamsInstance.toString();
        if (redirect) pushNavigator(undefined, shallow);

        return queryString;
      },
      getParams(except: string | string[] = []) {
        let keyArray = Array.from(queryParamsInstance.keys());
        if (Array.isArray(except)) {
          keyArray = keyArray.filter((keyItem) => !except.includes(keyItem));
        } else if (except) {
          keyArray = keyArray.filter((keyItem) => keyItem !== except);
        }
        return keyArray.reduce((acc: QueryParams, val) => {
          const paramValue = queryParamsInstance.getAll(val);
          acc[val] = paramValue.length > 1 ? paramValue : paramValue[0];
          return acc;
        }, {});
      },
      getAllParams: query,
    };
  }, [query, pathname, router]);

  return utils;
};

export default useQueryParams;
